<template>
  <div>
    <div class="dashboard-title dashboard-title--basic mb-lg">
      <div class="dashboard-title__actions">
        <div>
          <h1 class="fw-bold fs-xl mb-xs">Manage partners</h1>
          <p class="fc-darker fs-md mr-lg">
            Manage the profiles in your Technology Directory
          </p>
        </div>
        <div class="flex items-center">
          <a
            class="flex items-center pl-3 underline text-violet"
            target="_blank"
            :href="previewLink"
          >
            <span>Directory</span>
            <svg
              id="share"
              fill="#4d48e8"
              style="height: 24px; width: 24px; margin-left: 4px;"
              enable-background="new 0 0 300 300"
              height="512"
              viewBox="0 0 300 300"
              width="512"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="m224.172 254h-148.344c-16.445 0-29.828-13.383-29.828-29.828v-148.344c0-16.445 13.383-29.828 29.828-29.828h66.172c4.422 0 8 3.578 8 8s-3.578 8-8 8h-66.172c-7.625 0-13.828 6.203-13.828 13.828v148.344c0 7.625 6.203 13.828 13.828 13.828h148.344c7.625 0 13.828-6.203 13.828-13.828v-66.172c0-4.422 3.578-8 8-8s8 3.578 8 8v66.172c0 16.445-13.383 29.828-29.828 29.828z"
              />
              <g fill="#4d48e8">
                <path
                  d="m246 118c-4.422 0-8-3.578-8-8v-48h-48c-4.422 0-8-3.578-8-8s3.578-8 8-8h56c4.422 0 8 3.578 8 8v56c0 4.422-3.578 8-8 8z"
                />
                <path
                  d="m166 142c-2.047 0-4.094-.781-5.656-2.344-3.125-3.125-3.125-8.188 0-11.312l80-80c3.125-3.125 8.188-3.125 11.312 0s3.125 8.188 0 11.312l-80 80c-1.562 1.563-3.609 2.344-5.656 2.344z"
                />
              </g>
            </svg>
          </a>
          <Button
            v-on:button-click="$router.push('/agency/find-partners')"
            bMargin="left"
            >Add Partners</Button
          >
        </div>
      </div>
    </div>
    <TableToolbar :query="filters.name.value" @update-query="filterRows">
      <div>
        <label style="margin-bottom: 0">
          <div class="select select-mb-0">
            <select
              @change="filterRows(['status'], $event.target.value, 'status')"
            >
              <option selected="selected" value>Status</option>
              <option :selected="filters.status.value === 'P'" value="P"
                >Public</option
              >
              <option :selected="filters.status.value === 'F'" value="F"
                >Featured</option
              >
              <option :selected="filters.status.value === 'S'" value="S"
                >Searchable</option
              >
              <option :selected="filters.status.value === 'H'" value="H"
                >Hidden</option
              >
              <option :selected="filters.status.value === 'I'" value="I"
                >Invited</option
              >
            </select>
          </div>
        </label>
      </div>
    </TableToolbar>
    <div class="mb-lg">
      <v-table
        key="x"
        :data="rows"
        :filters="filters"
        :currentPage.sync="currentPage"
        :pageSize="pageSize"
        class="table table-bordered"
        @totalPagesChanged="totalPages = $event"
        :totalItemsInput="count"
      >
        <thead slot="head">
          <v-th
            v-on:sortChange="sortChange"
            v-for="(column, i) in columns"
            :key="i"
            :defaultSort="column.name === 'vendorName' ? 'asc' : undefined"
            :customSort="
              column.name === 'vendorName' ? sortVendorsAndInvites : undefined
            "
            :sortKey="column.name"
            class="mb-xxs grey-background fs-sm"
            >{{ column.displayName }}</v-th
          >
        </thead>
        <tbody
          slot="body"
          slot-scope="{ displayData }"
          v-if="displayData.length"
        >
          <tr v-for="(row, i) in displayData" :key="i" @click="navigate(row)">
            <td class="fs-sm f-capitalize fw-semibold">
              {{ row.vendorName || row.agencyTechPartnerCompanyName }}
            </td>
            <td class="fs-sm">
              <i class="fa fa-circle mr-xs" :class="row.status | circleClass" />
              {{ row.status | status }}
            </td>
          </tr>
        </tbody>
        <tbody
          style="padding: 1em; display: block;"
          class="fs-sm fw-semibold fc-medium"
          v-else
        >
          No partners!
        </tbody>
      </v-table>

      <smart-pagination
        :currentPage.sync="currentPage"
        :count="count"
        :pageSize="pageSize"
        v-on:update:currentPage="pageChanged($event)"
      />
    </div>
  </div>
</template>

<script>
import TableToolbar from '@/components/Dashboard/TableToolbar';
import { createNamespacedHelpers } from 'vuex';
import Button from '@/components/DesignSystem/Button';
const {
  mapGetters,
  mapActions,
  mapState,
  mapMutations,
} = createNamespacedHelpers('managePartners');

export default {
  name: 'Partners',
  components: {
    TableToolbar,
    Button,
  },
  computed: {
    ...mapGetters({
      columns: 'getColumns',
      rows: 'getRows',
      filters: 'getFilters',
      savedPage: 'getSavedPage',
    }),
    ...mapState(['count', 'pageSize']),
    previewLink() {
      if (this.$store.getters['settings'].customDomain) {
        return this.$store.getters['settings'].customDomain;
      }

      return 'http://' + this.directoryUrl + '.' + this.rootUrl;
    },
    rootUrl() {
      return process.env.VUE_APP_ROOT_CLIENT;
    },
    directoryUrl() {
      return this.$store.getters['settings'].partnerpageUrl;
    },
  },
  data() {
    return {
      currentPage: 1,
      totalPages: 3,
    };
  },
  created() {
    this.setSort('-created');
    this.fetchPartners(1);
    if (this.savedPage) {
      this.currentPage = this.savedPage;
    }
  },
  filters: {
    status(value) {
      switch (value) {
        case 'I':
          return 'Invited';
        case 'P':
          return 'Public';
        case 'H':
          return 'Hidden';
        case 'S':
          return 'Searchable';
        case 'N':
          return 'Initial';
        case 'F':
          return 'Featured';
        default:
          return 'Invited';
      }
    },
    circleClass(value) {
      switch (value) {
        case 'H':
          return 'fc-medium';
        case 'P':
          return 'fc-green';
        case 'S':
          return 'fc-green';
        case 'F':
          return 'fc-green';
        default:
          return 'fc-light';
      }
    },
  },
  methods: {
    ...mapActions([
      'updateQueryAndFilter',
      'fetchPartners',
      'savePage',
      'sortResults',
    ]),
    ...mapMutations(['setSort']),
    sortChange(key) {
      this.sortResults(key);
      this.currentPage = 1;
    },
    pageChanged(page) {
      this.fetchPartners(page);
    },
    filterRows(fields, query, type) {
      if (!fields && type === 'name') {
        fields = ['vendorName', 'agencyTechPartnerCompanyName'];
      }
      this.currentPage = 1;
      this.updateQueryAndFilter({ fields, query, type });
    },
    navigate(row) {
      row.agencyTechPartnerCompanyName
        ? this.$router.push(
            `/agency/our-directory/manage-partners/invitation/${row.id}`
          )
        : this.$router.push(`/agency/our-directory/manage-partners/${row.id}`);
    },
    sortVendorsAndInvites(a, b) {
      const first = a.vendorName || a.agencyTechPartnerCompanyName;
      const second = b.vendorName || b.agencyTechPartnerCompanyName;

      return first.localeCompare(second, undefined, {
        numeric: true,
        sensitivity: 'base',
      });
    },
  },
  beforeRouteLeave(to, from, next) {
    this.savePage(this.currentPage);
    next();
  },
};
</script>
